// shared/custom-ui/textarea/TextArea.tsx
import React, { useMemo, useState, forwardRef } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

interface TextAreaProps
    extends Omit<
        React.TextareaHTMLAttributes<HTMLTextAreaElement>,
        'size' | 'onChange'
    >,
    Omit<VariantProps<typeof taStyles>, 'error'> {
    label?: string;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
    rows?: number;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    resize?: boolean;
    maxLength?: number;
    showCharCount?: boolean;
    helperText?: string;
    size?: 'small' | 'medium' | 'large';
    variant?: 'default' | 'filled' | 'outlined';
    id?: string;
    name?: string;
    className?: string;
}

function cn(...parts: Array<string | false | null | undefined>) {
    return parts.filter(Boolean).join(' ');
}

const taStyles = tv({
    slots: {
        root: 'w-full',
        label:
            'mb-2 block text-[var(--v2-font-secondary)] font-semibold select-none text-[var(--v2-text-secondary)]',
        field:
            'w-full rounded-[var(--v2-radius-md)] transition-all duration-[var(--v2-duration-normal)] ease-[var(--v2-ease-calm)] ' +
            'placeholder:text-[var(--v2-text-muted)] focus:outline-none ' +
            'focus-visible:ring-[3px] focus-visible:ring-[var(--v2-primary-500)]/18 ' +
            'disabled:cursor-not-allowed disabled:opacity-100 text-[var(--v2-text-primary)] ' +
            'shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_0_rgba(255,255,255,0.015)]',
        footerRow: 'mt-1.5 flex items-start justify-between',
        helper: 'text-[var(--v2-font-secondary)] text-[var(--v2-text-secondary)]',
        error:
            'text-[var(--v2-font-secondary)] font-medium text-[var(--v2-danger)]/80 inline-flex items-center gap-1',
        counter: 'ml-2 flex-shrink-0 text-[var(--v2-font-secondary)] text-[var(--v2-text-muted)]',
    },
    variants: {
        size: {
            small: { field: 'px-3 py-2 text-[13px]' },
            medium: { field: 'px-3.5 py-2.5 text-sm' },
            large: { field: 'px-4 py-3 text-base' },
        },
        variant: {
            default: {
                field:
                    'bg-[var(--v2-bg-surface)] border border-[var(--v2-border-interactive)] hover:border-[var(--v2-border-glass-strong)]',
            },
            filled: {
                field:
                    'border border-transparent bg-[var(--v2-bg-hover)] hover:bg-[var(--v2-bg-card)]',
            },
            outlined: {
                field:
                    'bg-transparent border-2 border-[var(--v2-border-interactive)] hover:border-[var(--v2-border-glass-strong)]',
            },
        },
        error: {
            true: {},
        },
        disabled: {
            true: { field: 'bg-[var(--v2-bg-hover)] text-[var(--v2-text-muted)]' },
        },
        resize: {
            true: { field: 'resize-y' },
            false: { field: 'resize-none' },
        },
    },
    compoundVariants: [
        {
            error: true,
            variant: ['default', 'outlined'],
            class: {
                field:
                    'border-[var(--v2-danger)]/60 hover:border-[var(--v2-danger)]/60 focus-visible:ring-[var(--v2-danger)]/15',
            },
        },
        {
            error: true,
            variant: 'filled',
            class: {
                field:
                    'bg-[var(--v2-danger)]/5 hover:bg-[var(--v2-danger)]/5 focus-visible:ring-[var(--v2-danger)]/15',
            },
        },
    ],
    defaultVariants: {
        size: 'medium',
        variant: 'default',
        resize: true,
    },
});

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
    {
        label,
        value = '',
        onChange,
        placeholder = '',
        rows = 4,
        error = '',
        required = false,
        disabled = false,
        resize = true,
        maxLength,
        showCharCount = false,
        helperText,
        size = 'medium',
        variant = 'default',
        id,
        name,
        className,
        ...rest
    },
    ref
) {
    // Fallback unique ID generator
    const [generatedId] = useState(() => Math.random().toString(36).substr(2, 9));
    const textareaId = id || name || `ta-${generatedId}`;
    const errorId = `${textareaId}-error`;
    const helpId = `${textareaId}-help`;

    const [isFocused, setIsFocused] = useState(false);

    const {
        root,
        label: labelCls,
        field,
        footerRow,
        helper,
        error: errorCls,
        counter,
    } = taStyles({
        size,
        variant,
        error: Boolean(error),
        disabled,
        resize: resize ? true : false,
    });

    // Tailwind can't compute min height from expression; do it inline
    const minHeight = useMemo(() => Math.max(rows, 1) * 20 + 16 /* padding-ish */, [rows]);

    const describedBy = [
        error ? errorId : '',
        !error && helperText ? helpId : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={cn(root(), className)}>
            {label && (
                <label htmlFor={textareaId} className={labelCls()}>
                    {label}
                    {required && <span className="ml-1 text-red-500">*</span>}
                </label>
            )}

            <div className="relative">
                <textarea
                    id={textareaId}
                    ref={ref}
                    name={name}
                    value={value}
                    onChange={onChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder={placeholder}
                    rows={rows}
                    maxLength={maxLength}
                    disabled={disabled}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy || undefined}
                    className={cn(
                        field(),
                        // subtle focus tint for non-error states
                        !error && isFocused && variant !== 'filled' && 'ring-2 ring-primary-200',
                    )}
                    style={{ minHeight }}
                    {...rest}
                />

                {/* Optional focus ring sheen for filled variant */}
                {!error && isFocused && variant === 'filled' && (
                    <span className="pointer-events-none absolute inset-0 rounded-lg ring-2 ring-primary-200/70" />
                )}
            </div>

            <div className={footerRow()}>
                <div className="flex-1">
                    {error ? (
                        <p id={errorId} className={errorCls()}>
                            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16Zm1-5a1 1 0 11-2 0 1 1 0 012 0Zm-1-8a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1Z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            {error}
                        </p>
                    ) : helperText ? (
                        <p id={helpId} className={helper()}>
                            {helperText}
                        </p>
                    ) : null}
                </div>

                {showCharCount && maxLength != null && (
                    <p className={counter()} aria-live="polite">
                        {(value ?? '').length}/{maxLength}
                    </p>
                )}
            </div>
        </div>
    );
});

export default TextArea;
